import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';
import { getGrade, getLevelInfo } from '../utils/xpUtils';

const UserAvatar = ({ user, size = 8 }) => {
  const [failed, setFailed] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();
  if (user?.avatar && !failed) {
    return (
      <img src={user.avatar} alt={user.username}
        className={`w-${size} h-${size} rounded-full object-cover bg-zinc-800 shrink-0`}
        onError={() => setFailed(true)} />
    );
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-f1red/20 border border-f1red/40 flex items-center justify-center shrink-0`}>
      <span className="text-f1red font-black text-xs">{initials}</span>
    </div>
  );
};

const RANK_STYLE = {
  1: 'text-yellow-400 font-black',
  2: 'text-zinc-300 font-black',
  3: 'text-orange-400 font-black',
};

const medal = (rank) => (
  <span className={RANK_STYLE[rank] || 'text-zinc-500 font-medium'}>
    {rank <= 3 ? `P${rank}` : rank}
  </span>
);

const Leaderboard = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [season] = useState(new Date().getFullYear());

  useEffect(() => {
    api.get(`/leaderboard?season=${season}`)
      .then(res => setBoard(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [season]);

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-zinc-400">Chargement...</div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-white">Classement général</h1>
        <p className="text-zinc-400 mt-1">Saison {season}</p>
      </div>

      {board.length === 0 ? (
        <div className="card text-center text-zinc-400 py-12">
          <p className="text-lg mb-2">Aucun score enregistré pour l'instant.</p>
          <p className="text-sm">Les scores sont calculés automatiquement après chaque course.</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium w-12">#</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Joueur</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Courses</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Points</th>
              </tr>
            </thead>
            <tbody>
              {board.map((entry, idx) => {
                const isMe = user && entry.user._id === user.id;
                return (
                  <tr
                    key={entry.user._id}
                    className={`border-b border-zinc-800 last:border-0 ${isMe ? 'bg-f1red/5' : 'hover:bg-zinc-800/40'} transition-colors`}
                  >
                    <td className="px-5 py-4 text-center font-bold text-lg">
                      {medal(idx + 1)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={entry.user} size={8} />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold truncate ${isMe ? 'text-f1red' : 'text-white'}`}>
                              {entry.user.username}
                            </span>
                            {isMe && <span className="text-xs text-f1red bg-f1red/10 px-1.5 py-0.5 rounded shrink-0">Toi</span>}
                          </div>
                          {(() => { const g = getGrade(getLevelInfo(entry.user?.xp ?? entry.totalScore).level); return (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded w-fit"
                              style={{ color: g.color, background: g.bg }}>{g.label}</span>
                          ); })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-zinc-400">{entry.racesPlayed}</td>
                    <td className="px-5 py-4 text-right font-bold text-white text-lg">{entry.totalScore}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 card">
        <h3 className="font-bold text-white mb-3 text-sm">Système de points</h3>
        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
          {[
            [<><Icon name="trophy" size={12} className="inline mr-1" />Vainqueur</>, '25 pts'],
            [<><Icon name="trophy" size={12} className="inline mr-1 text-zinc-400" />P2</>, '18 pts'],
            [<><Icon name="trophy" size={12} className="inline mr-1 text-orange-400" />P3</>, '15 pts'],
            [<><Icon name="clock" size={12} className="inline mr-1" />Meilleur tour course</>, '12 pts'],
            [<><Icon name="lightning" size={12} className="inline mr-1" />Pole position</>, '10 pts'],
            [<><Icon name="clock" size={12} className="inline mr-1" />Meilleur tour qualif</>, '8 pts'],
            [<><Icon name="warning" size={12} className="inline mr-1" />Safety Car (oui/non)</>, '4 pts'],
            [<><Icon name="clock" size={12} className="inline mr-1" />Virtual Safety Car (oui/non)</>, '2 pts'],
            [<><Icon name="flag" size={12} className="inline mr-1" />Drapeau rouge (oui/non)</>, '10 pts'],
            ['Q1 éliminé (par pilote)', '1 pt'],
            ['Q2 éliminé (par pilote)', '2 pts'],
            ['Sprint Vainqueur', '12 pts'],
            ['Sprint P2', '10 pts'],
            ['Sprint P3', '8 pts'],
          ].map(([label, pts], i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="flex items-center gap-1">{label}</span>
              <span className="text-white font-semibold">{pts}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
